import { get, isEmpty, mapValues, set } from "lodash";
import validate from "validate.js";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

export const voucherValidationSchema = {
    "deal.totalKmCommission": {
        ...constraints.required,
        ...constraints.number,
    },
    paymentMethod: {
        ...constraints.required,
    },
    submittedDate: {
        ...constraints.required,
        ...constraints.date,
    },
};

export const voucherNonRequiredConstraints = {
    "deal.totalKmCommission": {
        ...constraints.number,
    },
    submittedDate: {
        ...constraints.date,
    },
};

export const consultingDealValidationSchema = {
    "deal.consultingDealType": {
        ...constraints.required,
    },
    "deal.consultingDealName": {
        ...constraints.required,
    },
    "deal.clientName": {
        ...constraints.required,
    },
};
export const installmentValidationSchema = {
    amount: {
        ...constraints.required,
        ...constraints.number,
    },
    dueDate: {
        ...constraints.required,
        ...constraints.date,
    },
};

export const installmentNonRequiredConstraints = {
    amount: {
        ...constraints.number,
    },
    dueDate: {
        ...constraints.date,
    },
};

export const splitNonRequiredConstraints = {
    netPercent: {
        ...constraints.number,
    },
    netAmount: {
        ...constraints.number,
    },
};

export const splitValidationSchema = {
    firstName: {
        ...constraints.required,
    },
    netPercent: {
        ...constraints.required,
        ...constraints.number,
    },
    representationType: {
        ...constraints.required,
    },
    netAmount: {
        ...constraints.required,
        ...constraints.number,
    },
    department: {
        ...constraints.required,
    },
};

const outsideSplitValidationSchema = {
    firstName: {
        ...constraints.required,
    },
    netPercent: {
        ...constraints.required,
        ...constraints.number,
    },
    representationType: {
        ...constraints.required,
    },
    netAmount: {
        ...constraints.required,
        ...constraints.number,
    },
};

export const runnerValidationSchema = {
    firstName: {
        ...constraints.required,
    },
    percentOfBrokerNet: {
        ...constraints.number,
    },
    amount: {
        ...constraints.number,
    },
};

export const runnerNonRequiredConstraints = {
    percentOfBrokerNet: {
        ...constraints.number,
    },
    amount: {
        ...constraints.number,
    },
};

export const splitModalNonRequiredConstraints = {
    netPercent: {
        ...constraints.number,
    },
    netAmount: {
        ...constraints.number,
    },
    companyZip: {
        ...constraints.id,
    },
};

export const splitModalValidationSchema = {
    firstName: {
        ...constraints.required,
    },
    netPercent: {
        ...constraints.required,
        ...constraints.number,
    },
    netAmount: {
        ...constraints.required,
        ...constraints.number,
    },
    companyName: {
        ...constraints.required,
    },
    companyAddress: {
        ...constraints.required,
    },
    companyCity: {
        ...constraints.required,
    },
    companyState: {
        ...constraints.required,
    },
    companyZip: {
        ...constraints.required,
        ...constraints.id,
    },
    representationType: {
        ...constraints.required,
    },
};

const addlSplitModalValidationWhenFreeFormDisabledSchema = {
    commissionComment: {
        ...constraints.required,
    },
};

const validateVoucher = (voucher, transactionType) => {
    let whatToValidate = {};
    const isConsulting = transactionType === "Consulting";

    whatToValidate = { ...voucherValidationSchema, ...(isConsulting ? consultingDealValidationSchema : {}) };
    return validate(voucher, whatToValidate, { fullMessages: false });
};

const validateInstallment = (installment) => {
    return validate(installment, installmentValidationSchema, { fullMessages: false });
};

const validateSplit = (split) => {
    return validate(split, splitValidationSchema, { fullMessages: false });
};

const validateOutsideSplit = (split) => {
    return validate(split, outsideSplitValidationSchema, { fullMessages: false });
};
const validateRunner = (runner) => {
    return validate(runner, runnerValidationSchema, { fullMessages: false });
};

const validateSplitModal = (values, commissionType) => {
    let errors = { ...validate(values, splitModalValidationSchema, { fullMessages: false }) };
    if (commissionType === "REFERRAL") {
        errors = {
            ...errors,
            ...validate(values, addlSplitModalValidationWhenFreeFormDisabledSchema, { fullMessages: false }),
        };
    }
    return errors;
};

const validateInstallments = (installments, currentInstallment) => {
    let errors = {};
    if (installments) {
        for (let i = currentInstallment - 1; i < installments.length; i++) {
            const validationResponse = validateInstallment(installments[i]);
            if (validationResponse) {
                set(errors, [installments[i].uniqueId], {
                    ...mapValues(validationResponse, (value) => value[0]),
                });
            }
        }
    }
    return isEmpty(errors) ? null : errors;
};

const validateVoucherByField = (section, id, value, voucher) => {
    switch (section) {
        case "installment": {
            const field = id.split(".").pop();
            const { result } = validateFieldWithKey(installmentValidationSchema, field, value);
            const fullKey = id.startsWith("deal.") ? id : "deal." + id;
            return [{ fullKey, resolved: isEmpty(result) }];
        }
        case "splitValidation": {
            const field = id.split(".").pop();
            const splitKey = id.slice(0, id.lastIndexOf("."));

            if (field === "netPercent" || field === "netAmount") {
                const split = get(voucher, `brokerCommissions.${splitKey}`, {});
                return ["netPercent", "netAmount"].map((f) => {
                    const { result } = validateFieldWithKey(splitValidationSchema, f, get(split, f));
                    return { fullKey: `${splitKey}.${f}`, resolved: isEmpty(result) };
                });
            }

            const { result } = validateFieldWithKey(splitValidationSchema, field, value);
            return [{ fullKey: id, resolved: isEmpty(result) }];
        }
        case "runnerValidation": {
            const field = id.split(".").pop();
            const { result } = validateFieldWithKey(runnerValidationSchema, field, value);
            return [{ fullKey: id, resolved: isEmpty(result) }];
        }
        case "splitModalValidation": {
            const field = id.split(".").pop();
            const { result } = validateFieldWithKey(splitModalValidationSchema, field, value);
            return [{ fullKey: id, resolved: isEmpty(result) }];
        }
        default: {
            const transactionType = get(voucher, "deal.transactionType", "");
            const schema = {
                ...voucherValidationSchema,
                ...(transactionType === "Consulting" ? consultingDealValidationSchema : {}),
            };
            const { fullKey, result } = validateFieldWithKey(schema, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
    }
};

export default {
    validateVoucher,
    validateSplit,
    validateRunner,
    validateSplitModal,
    validateInstallments,
    validateOutsideSplit,
    validateVoucherByField,
};
